import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    GetLedgerSettingsResponse,
    TaxRateSetResponse,
    TaxSettingsHistoryResponse,
} from '@/api/handlers/resources/settings';
import type { ApiResponse } from '@/api/handlers/responses';
import { SettingsQueryKeys } from './settingsQueryKeys';

/**
 * The tax rate set in force **today**.
 *
 * A 404 here is a documented outcome, not a failure — nothing is in force
 * either because no set has ever been recorded or because every recorded set
 * is future-dated. The two are told apart by consulting the history, so this
 * hook does not retry: a retry cannot change either answer.
 *
 * `enabled` exists for callers that read rates only as a *caption* on figures
 * they may not be allowed to see — the salaries register, whose every endpoint
 * is OrgAdmin-only. Labelling a screen the caller cannot read is a request for
 * nothing.
 */
export const useCurrentTaxSettingsQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<TaxRateSetResponse>>({
        queryKey: SettingsQueryKeys.current(),
        queryFn: () => apiClient.settings.getCurrentTaxSettings(),
        staleTime: 5 * 60_000,
        gcTime: Infinity,
        enabled,
    });
};

/**
 * The full change history, newest first.
 *
 * Loaded alongside the current set rather than on demand, because the write
 * path needs it: the earliest legal `effectiveFrom` for a new change is the day
 * after the latest recorded set, including a future-dated one.
 */
export const useTaxSettingsHistoryQuery = () => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<TaxSettingsHistoryResponse>>({
        queryKey: SettingsQueryKeys.history(),
        queryFn: () => apiClient.settings.getTaxSettingsHistory(),
        staleTime: 5 * 60_000,
        gcTime: Infinity,
    });
};

/**
 * The rates in force today, flattened to the pre-BOS-90 shape.
 *
 * Read by the dividends screens, which want "the current withholding rate" to
 * prefill a shareholder rather than a set resolved for a date. The settings
 * screen does **not** use this — it needs the set's id and effective date, which
 * this shape does not carry.
 */
export const useLedgerSettingsQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetLedgerSettingsResponse>>({
        queryKey: SettingsQueryKeys.all,
        queryFn: () => apiClient.settings.getLedgerSettings(),
        enabled,
        staleTime: 5 * 60_000,
        gcTime: Infinity,
    });
};

/**
 * The set in force on one date — the "what rate did July's payroll use" lookup.
 *
 * Disabled until a date is chosen. A too-early date answers 400
 * `NO_RATES_IN_FORCE_ON_DATE`, which the screen presents as an expected result
 * rather than an error, so this does not retry either.
 */
export const useTaxSettingsOnDateQuery = (date: string | null) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<TaxRateSetResponse>>({
        queryKey: SettingsQueryKeys.onDate(date ?? ''),
        queryFn: () => apiClient.settings.getTaxSettingsOnDate(date as string),
        enabled: date !== null,
        staleTime: 5 * 60_000,
        gcTime: Infinity,
    });
};
